import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { LooksGood, SubmitForm } from '../../../../../../constants';
// import { Options } from '../../../../../../data/formsAndTable/forms/FormControls';
import {
  AddUserFormInputs,
  AddUserInput,
  FormInputs,
  BaseInput,
  AddCourseInput,
  SelectFormOption,
} from '../../../../../../types/Forms';
import { Path, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useEffect, useMemo } from 'react';
import Select, { MultiValue, SingleValue } from 'react-select';
// import { editUser } from '../../../../../../reduxToolkit/reducers/contactSlice';

const getUserValidationRules = (el: AddUserInput, mode: 'create' | 'edit', t: TFunction) => {
  const rules: Record<string, any> = {};

  if (el.slug === 'password') {
    if (mode === 'create') {
      rules.required = t('courses.coursesList.inputs.requiredField', { field: el.title });
    }
  } else if (el.slug !== 'photo' && el.slug !== 'courseImage') {
    rules.required = t('courses.coursesList.inputs.requiredField', { field: el.title });
  }

  if (el.type === 'email') {
    rules.pattern = {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: t('courses.coursesList.inputs.invalidEmailFormat'),
    };
  }
  if (el.slug === 'telegram') {
    rules.required = t('courses.coursesList.inputs.requiredField', { field: el.title });
    rules.pattern = {
      value: /^@[\w\d_]{5,32}$/,
      message: t('courses.coursesList.inputs.invalidTelegramFormat'),
    };
  }
  return rules;
};
const getCourseValidationRules = (el: AddCourseInput, t: TFunction) => {
  const rules: Record<string, any> = {};
  if (el.slug !== 'id') {
    rules.required = t('courses.coursesList.inputs.requiredField', { field: el.title });
  }
  return rules;
};
interface FormCardProps<T> {
  inputs: BaseInput[];
  submitHandler: (data: T) => void;
  defaultValues?: Partial<T>;
  mode: 'create' | 'edit';
  subject: 'user' | 'course' | 'feedback';
  coursesList?: {
    value: string;
    label: string;
  }[];
}

const FormCard = <T extends Record<string, any>>({
  inputs,
  submitHandler,
  defaultValues,
  mode,
  subject,
  coursesList,
}: FormCardProps<T>) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<T>({
    mode: 'onBlur',
    defaultValues: defaultValues as import('react-hook-form').DefaultValues<T>,
  });
  const watchedValues = watch();
  const formValid = useMemo(() => {
    // Check all base inputs (excluding image inputs)
    for (const input of inputs) {
      const value = watchedValues[input.slug as keyof T];
      if (input.slug === 'photo' || input.slug === 'courseImage' || input.disabled) {
        continue;
      }

      if (input.type === 'select') {
        if (Array.isArray(value)) {
          // Multi-select must have at least one selected option
          if (value.length === 0) return false;
          if (value.length === 1 && typeof value[0] === 'object' && 'value' in value[0]) {
            // If it's an array of objects with a value property, check that value is not empty
            if (value[0].value == null || value[0].value === '') return false;
          }
        } else {
          // Single-select must have a value (not null/undefined/empty)
          if (value == null || value === '') return false;
        }
        if (!value || value === '' || value === 0 || value === '0') return false;
      } else if (!value || value === '') {
        return false;
      }
    }
    // For course with categories
    if (subject === 'course' && Array.isArray(watchedValues.categories)) {
      const seen = new Set<number>();

      for (const category of watchedValues.categories) {
        // required names
        if (!category?.name || !category?.name_uk || !category?.name_ru) return false;

        // normalize to number
        const raw =
          typeof category.category_pos === 'string'
            ? category.category_pos.trim()
            : category.category_pos;

        const pos = Number(raw);

        // must be a positive integer
        if (!Number.isInteger(pos) || pos <= 0) return false;

        // duplicate check
        if (seen.has(pos)) return false;
        seen.add(pos);
      }
    }

    return true;
  }, [watchedValues, inputs, subject]);

  const { fields, append, remove } = useFieldArray<T>({
    control,
    name: 'categories' as import('react-hook-form').ArrayPath<T>,
  });
  const onSubmit = (data: T) => {
    submitHandler(data);
  };

  const getSelectedIds = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && 'value' in value[0]) {
        const s = value.map((item) => String(item.value));
        return s;
      }
      const s = value.map((v) => String(v));

      return s;
    }
    return [];
  };

  return (
    <Form className="needs-validation custom-input" onSubmit={handleSubmit(onSubmit)}>
      <Row className="g-3">
        {inputs.map((el) => {
          const slug = el.slug as keyof T;
          const rules =
            mode === 'create' || mode === 'edit'
              ? typeof el.options !== 'undefined' || el.slug === 'password'
                ? getUserValidationRules(el as AddUserInput, mode, t)
                : getCourseValidationRules(el as AddCourseInput, t)
              : {};
          // if (mode === 'edit' && slug === 'password') {
          //   return disabled;
          // }

          return (
            <Col
              xs={el.slug === 'photo' || el.slug === 'courseImage' ? '4' : '3'}
              key={slug as string}
            >
              <Label>{el.label}</Label>
              {el.slug === 'photo' || el.slug === 'courseImage' ? (
                <input
                  type="file"
                  accept="image/*"
                  className={`form-control ${errors[slug] ? 'is-invalid' : 'is-valid'}`}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setValue(slug as import('react-hook-form').Path<T>, file as any);
                    }
                  }}
                />
              ) : el.type === 'select' && el.options ? (
                <Controller
                  control={control}
                  name={slug as import('react-hook-form').Path<T>}
                  rules={rules}
                  render={({ field }) => {
                    const isMulti = slug === 'coursesOptions';
                    const options =
                      slug === 'coursesOptions' && coursesList && coursesList.length > 0
                        ? coursesList
                        : (el.options ?? []);
                    const selectedIds = getSelectedIds(field.value);

                    const currentValue = isMulti
                      ? options.filter((opt) => selectedIds.includes(String(opt.value)))
                      : options.find((opt) => String(opt.value) === String(field.value)) || null;

                    return (
                      <Select
                        isMulti={isMulti}
                        options={options}
                        value={currentValue}
                        onChange={(selected: any) => {
                          if (Array.isArray(selected)) {
                            // Мультивибір: масив об'єктів
                            field.onChange(selected.map((o) => o.value));
                          } else {
                            // Одиночний вибір: об'єкт або null
                            field.onChange(selected?.value ?? '');
                          }
                        }}
                        onBlur={field.onBlur}
                        isDisabled={el.disabled}
                        placeholder={el.placeholder || 'Select'}
                        classNamePrefix="react-select"
                        className={errors[slug] ? 'is-invalid' : ''}
                      />
                    );
                  }}
                />
              ) : (
                <input
                  type={el.type}
                  disabled={(mode === 'edit' && el.slug === 'email') || el.disabled}
                  className="form-control"
                  placeholder={el.placeholder}
                  {...register(slug as Path<T>, rules)}
                />
              )}
              {errors[slug] && <FormFeedback>{String(errors[slug]?.message)}</FormFeedback>}
            </Col>
          );
        })}

        {subject === 'course' && (
          <>
            <Label>{t('courses.coursesList.inputs.labelCourseCategories')}</Label>
            {fields.map((field, index) => (
              // <Card  >
              <Col key={field.id} sm="6">
                <Row className="g-2 align-items-center mb-1">
                  <Col xs="12">
                    <Label>{t('courses.coursesList.inputs.placeholderCourseCategories')}</Label>
                    <Controller
                      control={control}
                      name={`categories.${index}.name` as Path<T>}
                      rules={{ required: t('courses.coursesList.inputs.requiredCourseCategory') }}
                      render={({ field, fieldState }) => (
                        <Input
                          className={`form-control ${fieldState.invalid ? 'is-invalid' : ''}`}
                          {...field}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row className="g-2 align-items-center mb-1">
                  <Col xs="12">
                    <Label>{t('courses.coursesList.inputs.placeholderCourseCategories_uk')}</Label>
                    <Controller
                      control={control}
                      name={`categories.${index}.name_uk` as Path<T>}
                      rules={{ required: t('courses.coursesList.inputs.requiredCourseCategory') }}
                      render={({ field, fieldState }) => (
                        <Input
                          className={`form-control ${fieldState.invalid ? 'is-invalid' : ''}`}
                          {...field}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row className="g-2 align-items-center mb-1">
                  <Col xs="12">
                    <Label>{t('courses.coursesList.inputs.placeholderCourseCategories_ru')}</Label>
                    <Controller
                      control={control}
                      name={`categories.${index}.name_ru` as Path<T>}
                      rules={{ required: t('courses.coursesList.inputs.requiredCourseCategory') }}
                      render={({ field, fieldState }) => (
                        <Input
                          className={`form-control ${fieldState.invalid ? 'is-invalid' : ''}`}
                          {...field}
                        />
                      )}
                    />
                  </Col>
                </Row>{' '}
                <Row className="g-2 align-items-center mb-1">
                  <Col xs="12">
                    <Label>
                      {
                        t(
                          'courses.coursesList.inputs.labelCourseCategoryPos',
                        ) /* e.g. "Category position" */
                      }
                    </Label>
                    <Controller
                      control={control}
                      name={`categories.${index}.category_pos` as Path<T>}
                      rules={{
                        required: 'Position is required',
                        validate: (v) => {
                          if (v === '' || v === undefined || v === null) return true; // optional
                          if (Number.isNaN(Number(v)))
                            return t('courses.coursesList.inputs.invalidNumber');
                          const n = Number(v);
                          if (!Number.isInteger(n) || n <= 0)
                            return t('courses.coursesList.inputs.mustBePositiveInteger');
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          className={`form-control ${fieldState.invalid ? 'is-invalid' : ''}`}
                          {...field}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Col xs="2" className="d-flex align-items-end mt-2">
                  <Button color="danger" type="button" onClick={() => remove(index)}>
                    −
                  </Button>
                </Col>
              </Col>
              // </Card>
            ))}
            <Col sm="3">
              <Button
                type="button"
                onClick={() =>
                  append({ name: '', name_uk: '', name_ru: '', category_pos: '' } as never)
                }
              >
                {t('courses.coursesList.coursesCreateCategoryBtn')}
              </Button>
            </Col>

            {/* <Col xs="12">
              <Button type="submit" color="primary">
                {mode === 'create' ? t('global.create') : t('global.update')}
              </Button>
            </Col> */}
          </>
        )}
        <Col xs="12">
          <Button type="submit" color="primary" disabled={!formValid}>
            {mode === 'create' ? t('global.create') : t('global.update')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default FormCard;
